import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, Users, TrendingUp, Clock, Shield,
  Activity, Ticket, Mail, DollarSign, BarChart3, ExternalLink, Phone,
  Send, Inbox, ChevronRight, UserCircle, Brain, Lock, Globe, FileText,
  ShieldCheck, ArrowUpRight,
} from 'lucide-react';
import { useFirmKnowledge } from '../../../api/firm-knowledge';
import {
  accountsApi,
  type AccountUser,
  type AccountDetail,
  type AccountContact,
  type AccountEmail,
} from '../../../api/accounts';
import { WorkOSBadge, type WorkOSStatus } from '../../../components/shared/WorkOSBadge';
import UserDetailPanel from '../components/UserDetailPanel';

// ─── Helpers ────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 70) return 'text-emerald-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-600';
}

function scoreBg(score: number): string {
  if (score >= 70) return 'bg-emerald-50 border-emerald-200';
  if (score >= 40) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
}

function riskBadge(level: string) {
  const styles: Record<string, string> = {
    healthy: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
    at_risk: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
    critical: 'bg-red-50 text-red-700 ring-1 ring-red-600/20',
  };
  const labels: Record<string, string> = { healthy: 'Healthy', at_risk: 'At Risk', critical: 'Critical' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[level] ?? 'bg-neutral-100 text-neutral-600'}`}>
      {labels[level] ?? level}
    </span>
  );
}

function sourceBadge(source: string) {
  const s = source.toLowerCase();
  if (s === 'hubspot') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 ring-1 ring-orange-200">
        HubSpot
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-neutral-100 text-neutral-500">
      {source}
    </span>
  );
}


function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}


function userAvatar(user: AccountUser) {
  if (user.profile_picture_url) {
    return (
      <img
        src={user.profile_picture_url}
        alt={`${user.first_name} ${user.last_name}`}
        className="w-8 h-8 rounded-full object-cover"
      />
    );
  }
  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-semibold">
      {initials}
    </div>
  );
}

function contactInitials(contact: AccountContact): string {
  return `${contact.first_name?.[0] ?? ''}${contact.last_name?.[0] ?? ''}`.toUpperCase();
}


function formatRevenue(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
}

// ─── Stat Card ──────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, subtitle }: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4">
      <div className="flex items-center gap-1.5 text-neutral-400 mb-2">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-xl font-bold text-neutral-900">{value}</div>
      {subtitle && <p className="text-xs text-neutral-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ─── Firm Knowledge Section ──────────────────────────────────────────────────

function FirmKnowledgeSection({ workspaceSlug, accountName }: { workspaceSlug: string | null; accountName: string }) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useFirmKnowledge(workspaceSlug ?? '');

  const fk = data?.data;

  const editUrl = workspaceSlug ? `/w/${workspaceSlug}/ar/kf` : null;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm mb-6">
      <div className="px-5 py-3.5 border-b border-neutral-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={14} className="text-violet-500" />
          <h2 className="text-sm font-semibold text-neutral-900">Firm Knowledge</h2>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 font-medium uppercase tracking-wide">Archie</span>
        </div>
        {editUrl && (
          <button
            onClick={() => navigate(editUrl)}
            className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium transition-colors"
          >
            Edit in Archie <ArrowUpRight size={11} />
          </button>
        )}
      </div>

      {!workspaceSlug ? (
        <div className="px-5 py-8 text-center">
          <Brain size={28} className="mx-auto text-neutral-200 mb-2" />
          <p className="text-sm font-medium text-neutral-500 mb-1">No workspace connected</p>
          <p className="text-xs text-neutral-400">
            Link {accountName} to a Martha workspace to configure Archie's firm knowledge context.
          </p>
        </div>
      ) : isLoading ? (
        <div className="px-5 py-6 space-y-3 animate-pulse">
          {[1, 2, 3].map(i => <div key={i} className="h-4 bg-neutral-100 rounded w-2/3" />)}
        </div>
      ) : isError || !fk ? (
        <div className="px-5 py-8 text-center">
          <Brain size={28} className="mx-auto text-neutral-200 mb-2" />
          <p className="text-sm text-neutral-400">No firm knowledge configured yet.</p>
          {editUrl && (
            <button
              onClick={() => navigate(editUrl)}
              className="mt-3 text-xs text-violet-600 hover:text-violet-700 font-medium"
            >
              Configure now →
            </button>
          )}
        </div>
      ) : (
        <div className="px-5 py-4 grid grid-cols-1 min-[640px]:grid-cols-2 min-[960px]:grid-cols-4 gap-5">

          {/* Practice Profile */}
          <div>
            <div className="flex items-center gap-1.5 text-neutral-400 mb-2">
              <Building2 size={12} />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Practice Profile</span>
            </div>
            {fk.profile?.practice_name ? (
              <div className="space-y-1">
                <p className="text-sm font-medium text-neutral-900">{fk.profile.practice_name}</p>
                {fk.profile.legal_name && fk.profile.legal_name !== fk.profile.practice_name && (
                  <p className="text-xs text-neutral-500">{fk.profile.legal_name}</p>
                )}
                {fk.profile.jurisdiction && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600">
                    {fk.profile.jurisdiction}
                  </span>
                )}
                {(fk.profile.team_contacts?.length ?? 0) > 0 && (
                  <p className="text-xs text-neutral-400">{fk.profile.team_contacts!.length} key contact{fk.profile.team_contacts!.length !== 1 ? 's' : ''}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-neutral-400 italic">Not configured</p>
            )}
          </div>

          {/* Credentials */}
          <div>
            <div className="flex items-center gap-1.5 text-neutral-400 mb-2">
              <Lock size={12} />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Vault Keys</span>
            </div>
            {fk.credentials && Object.keys(fk.credentials).length > 0 ? (
              <div className="space-y-1">
                {Object.entries(fk.credentials).slice(0, 4).map(([role, key]) => (
                  <div key={role} className="flex items-center gap-1.5">
                    <Lock size={10} className="text-neutral-300 flex-shrink-0" />
                    <span className="text-[11px] font-mono text-neutral-600 truncate">{key}</span>
                  </div>
                ))}
                {Object.keys(fk.credentials).length > 4 && (
                  <p className="text-[10px] text-neutral-400">+{Object.keys(fk.credentials).length - 4} more</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-neutral-400 italic">No vault keys</p>
            )}
          </div>

          {/* Standing Policies */}
          <div>
            <div className="flex items-center gap-1.5 text-neutral-400 mb-2">
              <ShieldCheck size={12} />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Policies</span>
            </div>
            {(fk.policies?.length ?? 0) > 0 ? (
              <div className="space-y-1">
                {fk.policies!.slice(0, 3).map((p, i) => (
                  <p key={i} className="text-xs text-neutral-600 line-clamp-1">{p}</p>
                ))}
                {fk.policies!.length > 3 && (
                  <p className="text-[10px] text-neutral-400">+{fk.policies!.length - 3} more</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-neutral-400 italic">No policies</p>
            )}
          </div>

          {/* Integrations */}
          <div>
            <div className="flex items-center gap-1.5 text-neutral-400 mb-2">
              <Globe size={12} />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Integrations</span>
            </div>
            {fk.integrations ? (
              <div className="space-y-1">
                {fk.integrations.sharepoint?.site_url && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-xs text-neutral-600">SharePoint</span>
                  </div>
                )}
                {fk.integrations.ato?.agency_number && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-xs text-neutral-600">ATO Portal</span>
                  </div>
                )}
                {fk.integrations.xero?.org_id && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-xs text-neutral-600">Xero</span>
                  </div>
                )}
                {(fk.integrations.custom?.length ?? 0) > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    <span className="text-xs text-neutral-600">{fk.integrations.custom!.length} custom</span>
                  </div>
                )}
                {!fk.integrations.sharepoint?.site_url && !fk.integrations.ato?.agency_number && !fk.integrations.xero?.org_id && (fk.integrations.custom?.length ?? 0) === 0 && (
                  <p className="text-xs text-neutral-400 italic">None connected</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-neutral-400 italic">None connected</p>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/[A-Z]\s*\{[^}]*\}/g, '')   // inline CSS like "P {margin-top:0;...}"
    .replace(/\s+/g, ' ')
    .trim();
}

// ─── Main ───────────────────────────────────────────────────────────────────

export default function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<AccountUser | null>(null);

  const { data: account, isLoading, isError } = useQuery({
    queryKey: ['crm-account-detail', customerId],
    queryFn: () => accountsApi.get(customerId!),
    enabled: !!customerId,
    staleTime: 60_000,
  });

  const { data: contactsData } = useQuery({
    queryKey: ['crm-account-contacts', customerId],
    queryFn: () => accountsApi.listContacts(customerId!),
    enabled: !!customerId,
    staleTime: 60_000,
  });

  const { data: emailsData } = useQuery({
    queryKey: ['crm-account-emails', customerId],
    queryFn: () => accountsApi.listEmails(customerId!, 20),
    enabled: !!customerId,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="h-full bg-neutral-50 p-6">
        <div className="max-w-7xl mx-auto space-y-4 animate-pulse">
          <div className="h-4 w-28 bg-neutral-200 rounded" />
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-neutral-200 rounded-xl" />
            <div className="space-y-2">
              <div className="h-6 w-48 bg-neutral-200 rounded" />
              <div className="h-4 w-32 bg-neutral-100 rounded" />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 bg-neutral-200 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-neutral-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (isError || !account) {
    return (
      <div className="h-full bg-neutral-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 mb-4">
            <ArrowLeft size={14} /> Back
          </button>
          <p className="text-sm text-red-600">Failed to load customer details.</p>
        </div>
      </div>
    );
  }

  const contacts = contactsData?.data ?? [];
  const emails = emailsData?.data ?? [];
  const users = account.users ?? [];
  const activity = account.activity;
  const support = account.support ?? { open_tickets: 0, resolved_tickets: 0 };
  const deals = account.deals ?? { count: 0, won_revenue: 0, open_deals: 0 };
  const comms = account.comms ?? { email_count: 0, last_email_at: null };

  return (
    <div className="flex flex-col h-full bg-neutral-50">
      <div className="max-w-7xl mx-auto w-full px-6 py-6 flex-1 overflow-auto">
        {/* Back link */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 mb-5 transition-colors"
        >
          <ArrowLeft size={14} /> Back to Customers
        </button>

        {/* ── Header ── */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
            <Building2 size={22} className="text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl font-semibold text-neutral-900">{account.name}</h1>
              {riskBadge(account.risk_level)}
              {sourceBadge(account.record_source)}
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-neutral-500">
              {account.domain && <span>{account.domain}</span>}
              {account.onboarded_at && (
                <>
                  <span className="text-neutral-300">|</span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    Onboarded {formatDate(account.onboarded_at)}
                  </span>
                </>
              )}
              {account.last_login_at && (
                <>
                  <span className="text-neutral-300">|</span>
                  <span>Last active {relativeTime(account.last_login_at)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Quick Stats ── */}
        <div className="grid grid-cols-5 gap-2.5 mb-6">
          {/* Health Score */}
          <div className={`rounded-xl border p-3.5 ${scoreBg(account.health_score)}`}>
            <div className="flex items-center gap-1.5 text-neutral-500 mb-1.5">
              <TrendingUp size={13} />
              <span className="text-[11px] font-medium">Health</span>
            </div>
            <p className={`text-2xl font-bold tabular-nums ${scoreColor(account.health_score)}`}>
              {account.health_score}
            </p>
          </div>

          {/* Contacts */}
          <div className="rounded-xl border border-neutral-200 bg-white p-3.5">
            <div className="flex items-center gap-1.5 text-neutral-400 mb-1.5">
              <UserCircle size={13} />
              <span className="text-[11px] font-medium">Contacts</span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">{contacts.length}</p>
          </div>

          {/* Emails */}
          <div className="rounded-xl border border-neutral-200 bg-white p-3.5">
            <div className="flex items-center gap-1.5 text-neutral-400 mb-1.5">
              <Mail size={13} />
              <span className="text-[11px] font-medium">Emails</span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">{comms.email_count}</p>
            {comms.last_email_at && (
              <p className="text-[10px] text-neutral-400 mt-0.5">Last {relativeTime(comms.last_email_at)}</p>
            )}
          </div>

          {/* Tickets */}
          <div className="rounded-xl border border-neutral-200 bg-white p-3.5">
            <div className="flex items-center gap-1.5 text-neutral-400 mb-1.5">
              <Ticket size={13} />
              <span className="text-[11px] font-medium">Tickets</span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">
              {support.open_tickets}
              <span className="text-sm font-normal text-neutral-400"> open</span>
            </p>
            {support.resolved_tickets > 0 && (
              <p className="text-[10px] text-neutral-400 mt-0.5">{support.resolved_tickets} resolved</p>
            )}
          </div>

          {/* Revenue */}
          <div className="rounded-xl border border-neutral-200 bg-white p-3.5">
            <div className="flex items-center gap-1.5 text-neutral-400 mb-1.5">
              <DollarSign size={13} />
              <span className="text-[11px] font-medium">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tabular-nums">
              {deals.won_revenue > 0 ? formatRevenue(deals.won_revenue) : '-'}
            </p>
            {deals.open_deals > 0 && (
              <p className="text-[10px] text-neutral-400 mt-0.5">{deals.open_deals} open deal{deals.open_deals > 1 ? 's' : ''}</p>
            )}
          </div>
        </div>

        {/* ── Two-Column Layout ── */}
        <div className="grid grid-cols-1 min-[540px]:grid-cols-5 gap-5 mb-6">

          {/* Left Column — Contacts + Users (3/5 width) */}
          <div className="min-[540px]:col-span-3 space-y-5">

            {/* Contacts Section */}
            <div className="bg-white border border-neutral-200 rounded-xl shadow-sm">
              <div className="px-5 py-3.5 border-b border-neutral-100">
                <h2 className="text-sm font-semibold text-neutral-900">
                  Contacts <span className="text-neutral-400 font-normal">({contacts.length})</span>
                </h2>
              </div>
              {contacts.length === 0 ? (
                <div className="py-10 text-center">
                  <UserCircle size={28} className="mx-auto text-neutral-300 mb-2" />
                  <p className="text-sm text-neutral-400">No CRM contacts associated</p>
                </div>
              ) : (
                <div className="divide-y divide-neutral-50">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="px-5 py-3 flex items-center gap-3 hover:bg-neutral-50/60 transition-colors">
                      <div className="w-9 h-9 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {contactInitials(contact)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900">
                          {contact.first_name} {contact.last_name}
                        </p>
                        <p className="text-xs text-neutral-500 truncate">{contact.email}</p>
                      </div>
                      {contact.job_title && (
                        <span className="text-xs text-neutral-400 hidden sm:block">{contact.job_title}</span>
                      )}
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone}`}
                          className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
                          title={contact.phone}
                        >
                          <Phone size={13} />
                        </a>
                      )}
                      {contact.lifecycle_stage && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 capitalize hidden sm:inline">
                          {contact.lifecycle_stage}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Users (WorkOS) */}
            {users.length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-xl shadow-sm">
                <div className="px-5 py-3.5 border-b border-neutral-100">
                  <h2 className="text-sm font-semibold text-neutral-900">
                    Product Users <span className="text-neutral-400 font-normal">({users.length})</span>
                    <span className="ml-2 text-[10px] text-neutral-400 uppercase tracking-wider">WorkOS</span>
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-100 bg-neutral-50/50">
                        <th className="text-left px-5 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">Email</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">Role</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider">Last Active</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-50">
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          onClick={() => setSelectedUser(user)}
                          className="hover:bg-neutral-50/80 transition-colors cursor-pointer"
                        >
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-2.5">
                              {userAvatar(user)}
                              <span className="font-medium text-neutral-900">
                                {user.first_name} {user.last_name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-neutral-500">{user.email}</td>
                          <td className="px-4 py-2.5">
                            <span className="text-xs px-2 py-0.5 rounded-full capitalize bg-neutral-100 text-neutral-600">
                              {user.membership_role}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <WorkOSBadge status={user.workos_status as WorkOSStatus} />
                          </td>
                          <td className="px-4 py-2.5 text-neutral-500">
                            {relativeTime(user.last_sign_in_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Right Column — Emails + Activity (2/5 width) */}
          <div className="min-[540px]:col-span-2 space-y-5">

            {/* Recent Emails */}
            <div className="bg-white border border-neutral-200 rounded-xl shadow-sm">
              <div className="px-4 py-3 border-b border-neutral-100">
                <h2 className="text-sm font-semibold text-neutral-900">
                  Recent Emails <span className="text-neutral-400 font-normal">({emails.length})</span>
                </h2>
              </div>
              {emails.length === 0 ? (
                <div className="py-10 text-center">
                  <Mail size={24} className="mx-auto text-neutral-300 mb-2" />
                  <p className="text-sm text-neutral-400">No emails tracked</p>
                </div>
              ) : (
                <div className="divide-y divide-neutral-50 max-h-[500px] overflow-y-auto">
                  {emails.map((email) => (
                    <div key={email.id} className="px-4 py-3 hover:bg-neutral-50/60 transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        {email.direction === 'inbound' ? (
                          <Inbox size={12} className="text-blue-500 flex-shrink-0" />
                        ) : (
                          <Send size={12} className="text-emerald-500 flex-shrink-0" />
                        )}
                        <span className="text-xs font-medium text-neutral-700 truncate flex-1">
                          {email.from_name ?? email.from_address ?? 'Unknown'}
                        </span>
                        {email.sent_at && (
                          <span className="text-[10px] text-neutral-400 flex-shrink-0">
                            {relativeTime(email.sent_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-[13px] text-neutral-800 font-medium truncate mb-0.5">
                        {email.subject ?? '(no subject)'}
                      </p>
                      {email.body_preview && (
                        <p className="text-xs text-neutral-400 line-clamp-2">
                          {stripHtml(email.body_preview).slice(0, 120)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Activity (PostHog) — compact */}
            {activity ? (
              <div className="bg-white border border-neutral-200 rounded-xl shadow-sm">
                <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
                    <Activity size={13} className="text-blue-500" /> Usage
                  </h2>
                  <span className="text-[9px] text-neutral-400 uppercase tracking-wider">PostHog</span>
                </div>
                <div className="px-4 py-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-[10px] text-neutral-400 uppercase tracking-wider mb-0.5">Sessions</p>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums">{activity.sessions_30d.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-neutral-400 uppercase tracking-wider mb-0.5">Pageviews</p>
                      <p className="text-lg font-bold text-neutral-900 tabular-nums">{activity.pageviews_30d.toLocaleString()}</p>
                    </div>
                  </div>
                  {activity.top_pages.length > 0 && (
                    <>
                      <p className="text-[10px] text-neutral-400 uppercase tracking-wider mb-2">Top Pages (30d)</p>
                      <div className="space-y-1.5">
                        {activity.top_pages.slice(0, 5).map((tp, i) => {
                          const maxViews = activity.top_pages[0]?.views ?? 1;
                          const pct = Math.round((tp.views / maxViews) * 100);
                          return (
                            <div key={i}>
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs text-neutral-600 truncate max-w-[200px]">{tp.page}</span>
                                <span className="text-[10px] text-neutral-400 tabular-nums ml-2">{tp.views}</span>
                              </div>
                              <div className="h-1 bg-neutral-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-neutral-200 rounded-xl shadow-sm">
                <div className="px-4 py-3 border-b border-neutral-100">
                  <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
                    <Activity size={13} className="text-neutral-300" /> Usage
                  </h2>
                </div>
                <div className="px-4 py-6 text-center">
                  <p className="text-xs text-neutral-400">PostHog not connected</p>
                </div>
              </div>
            )}

            {/* Active Users mini stat (when no user table) */}
            {users.length === 0 && account.member_count > 0 && (
              <div className="bg-white border border-neutral-200 rounded-xl shadow-sm px-4 py-3">
                <div className="flex items-center gap-1.5 text-neutral-400 mb-1">
                  <Users size={13} />
                  <span className="text-[11px] font-medium">WorkOS Members</span>
                </div>
                <p className="text-lg font-bold text-neutral-900">
                  {account.active_members} <span className="text-sm font-normal text-neutral-400">of {account.member_count}</span>
                </p>
              </div>
            )}
          </div>
        </div>
        {/* ── Firm Knowledge ── */}
        <FirmKnowledgeSection workspaceSlug={account.workspace_slug ?? null} accountName={account.name} />

      </div>

      {/* User detail slide-out */}
      <UserDetailPanel user={selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
}
